/**
 * CampaignFormPage - Create or edit an email campaign
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getCampaign,
  createCampaign,
  updateCampaign,
  sendCampaign,
  sendTestCampaignEmail,
  stopCampaign,
  getShows,
  getCities,
  getCampaignTemplates,
  resolveCampaignTemplate,
  getCampaignAudience,
  getCampaignSends,
  updateAdminContact,
  batchUnsubscribeContacts,
  unscheduleChain,
  getAllTourStops,
} from '../../../api/admin';
import type { Show, City, CampaignTemplate, CampaignAudience, AdminCampaign, EmailSendRecord } from '../../../api/admin';
import RichTextEditor from '../../../components/RichTextEditor';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import { PER_RECIPIENT_VARS, emptyVariablesForTargeting, fallbackRenderLabel } from '../../../lib/campaignTemplateVariables';
import { naiveDatetimeToUTC, utcToNaiveDatetime } from '../../../utils/timezone';

const PREVIEW_SAMPLE_VALUES: Record<string, string> = {
  show_name: '[Show Name]',
  city: '[City]', state: '[ST]',
  theater_name: '[Theater]',
  available_dates: 'Saturdays: June 21, June 28, July 5',
  available_dates_short: 'June/July, 2025',
  time_slots: '9:30 AM – 11:00 AM',
  pricing: '$650', deposit_amount: '$100',
  questionnaire_url: `${window.location.origin}/events`,
  booking_url: `${window.location.origin}/events/8f14e45f-ceea-467f-9a2d-3b1c5d8e6a01?zip=10036`,
  studio_zip: '10036',
  upcoming_shows: 'Hairspray – June 14, [Same City]<br>Wicked – July 5, [Same City]',
  upcoming_shows_block: '<p style="font-size:15px;font-weight:700;margin:20px 0 4px 0;color:#222;"><strong>Upcoming Broadway Master Classes:</strong></p><p style="margin:0 0 20px 0;">Hairspray – June 14, [Same City]<br>Wicked – July 5, [Same City]</p>',
  contact_email: 'michele@stagedooraccess.com',
  contact_phone: '(415) 846-9450',
  contact_name: 'Jane', studio_name: 'Your Dance Studio',
  activation_url: `${window.location.origin}/activate?token=sample-token`,
  faculty_name: 'Jane Doe',
  invitation_url: `${window.location.origin}/faculty/invite?token=sample-token`,
};

const CAMPAIGN_TYPES = [
  { value: 'tour_announcement', label: 'Tour Announcement' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'general', label: 'General' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'activation', label: 'Activation' },
  { value: 'faculty_invitation', label: 'Faculty Invitation' },
];

const TEMPLATE_VARIABLES = [
  { value: '{{show_name}}', label: 'Show Name', description: 'Name of the target show' },
  { value: '{{city}}', label: 'City', description: 'City name' },
  { value: '{{state}}', label: 'State', description: 'State abbreviation' },
  { value: '{{theater_name}}', label: 'Theater Name', description: 'Venue/theater name' },
  { value: '{{available_dates}}', label: 'Available Dates', description: 'Saturdays in the tour stop date range' },
  { value: '{{available_dates_short}}', label: 'Dates (Short)', description: 'Compact date range, e.g. "June/July, 2025"' },
  { value: '{{time_slots}}', label: 'Time Slots', description: 'Session time range, e.g. "9:30 AM – 11:00 AM"' },
  { value: '{{pricing}}', label: 'Pricing', description: 'Starting price for classes' },
  { value: '{{deposit_amount}}', label: 'Deposit Amount', description: 'Deposit required per class ($100)' },
  { value: '{{upcoming_shows}}', label: 'Upcoming Shows', description: 'Comma-separated list of other upcoming shows in this campaign\'s city (empty if none)' },
  { value: '{{upcoming_shows_block}}', label: 'Upcoming Shows Block', description: 'Heading + list of other upcoming shows in this campaign\'s city. Renders empty when no other shows.' },
  { value: '{{booking_url}}', label: 'Booking Link', description: 'Personalized events page link (uses studio zip code)' },
  { value: '{{contact_email}}', label: 'Contact Email', description: 'Contact email address' },
  { value: '{{contact_phone}}', label: 'Contact Phone', description: 'Contact phone number' },
  { value: '{{studio_name}}', label: 'Studio Name', description: "Recipient studio's name" },
  { value: '{{contact_name}}', label: 'Contact Name', description: "Recipient contact's first name" },
  { value: '{{activation_url}}', label: 'Activation Link', description: 'Personalized account activation link for dormant studios' },
  { value: '{{faculty_name}}', label: 'Faculty Name', description: 'Name of the invited faculty member' },
  { value: '{{invitation_url}}', label: 'Invitation Link', description: 'Personalized faculty invitation acceptance link' },
];

const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern', abbr: 'ET' },
  { value: 'America/Chicago', label: 'Central', abbr: 'CT' },
  { value: 'America/Denver', label: 'Mountain', abbr: 'MT' },
  { value: 'America/Los_Angeles', label: 'Pacific', abbr: 'PT' },
] as const;

type USTimezone = (typeof US_TIMEZONES)[number]['value'];

/** Detect the user's browser timezone and map to the closest US timezone */
function detectUSTimezone(): USTimezone {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // Direct match
  const match = US_TIMEZONES.find((tz) => tz.value === browserTz);
  if (match) return match.value;

  // Map common US timezone aliases
  const mapping: Record<string, USTimezone> = {
    'US/Eastern': 'America/New_York',
    'US/Central': 'America/Chicago',
    'US/Mountain': 'America/Denver',
    'US/Pacific': 'America/Los_Angeles',
    'America/Detroit': 'America/New_York',
    'America/Indiana/Indianapolis': 'America/New_York',
    'America/Kentucky/Louisville': 'America/New_York',
    'America/Boise': 'America/Denver',
    'America/Phoenix': 'America/Denver',
  };
  if (mapping[browserTz]) return mapping[browserTz];

  // Fallback: estimate from UTC offset
  const offsetMinutes = new Date().getTimezoneOffset();
  if (offsetMinutes >= 240 && offsetMinutes <= 300) return 'America/New_York';
  if (offsetMinutes >= 300 && offsetMinutes <= 360) return 'America/Chicago';
  if (offsetMinutes >= 360 && offsetMinutes <= 420) return 'America/Denver';
  if (offsetMinutes >= 420 && offsetMinutes <= 480) return 'America/Los_Angeles';

  return 'America/New_York'; // default
}

/**
 * Format a UTC ISO string in a specific timezone for human display.
 * e.g., "2026-02-10T20:00:00.000Z" + "America/New_York" → "Feb 10, 3:00 PM"
 */
function formatInTimezone(utcISO: string, timezone: string): string {
  return new Date(utcISO).toLocaleString('en-US', {
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export default function CampaignFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState<string>('draft');
  const [error, setError] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<AdminCampaign | null>(null);
  // SDA-222: scroll the top-of-form error banner into view when an error is set,
  // so a validation refusal (e.g. the past-date block) isn't missed off-screen
  // when the user submits from the bottom of a long form.
  const errorBannerRef = useRef<HTMLDivElement | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [plainTextContent, setPlainTextContent] = useState('');
  const [campaignType, setCampaignType] = useState('general');
  const [scheduledFor, setScheduledFor] = useState(''); // naive datetime in selected tz
  const [scheduledTimezone, setScheduledTimezone] = useState<USTimezone>(
    detectUSTimezone()
  );

  // Targeting state
  const [selectedShowIds, setSelectedShowIds] = useState<string[]>([]);
  const [selectedCityIds, setSelectedCityIds] = useState<string[]>([]);
  // SDA-130: default both checkboxes to checked on NEW campaigns. Existing
  // campaigns still load whatever was saved in the DB via loadCampaign().
  const [excludeBooked, setExcludeBooked] = useState(true);
  const [includeInterested, setIncludeInterested] = useState(true);
  // SDA-188: gate the audience preview until the saved campaign (incl. its
  // targeting) has loaded, so we never fetch with empty targeting arrays — which
  // the preview endpoint would read as "no targeting → all opted-in" (the
  // spurious 9,436 count). Stays false for NEW campaigns (effect is gated on
  // isEditing anyway).
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [shows, setShows] = useState<Show[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [showSearch, setShowSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');

  // Variable picker, template, preview state
  const [showVariableHelp, setShowVariableHelp] = useState(false);
  const [showVariableMenu, setShowVariableMenu] = useState(false);
  const [pendingSend, setPendingSend] = useState(false);
  const [unresolvedShowVars, setUnresolvedShowVars] = useState<string[]>([]);
  const [perRecipientVarsState, setPerRecipientVarsState] = useState<string[]>([]);
  const [showScheduleConfirm, setShowScheduleConfirm] = useState(false);
  // resolvedVariables + resolvedSubject: populated when "Preview Email" loads real data
  const [resolvedVariables, setResolvedVariables] = useState<Record<string, string>>({});
  const [resolvedSubject, setResolvedSubject] = useState('');
  const [audience, setAudience] = useState<CampaignAudience | null>(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [showAudienceModal, setShowAudienceModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState<CampaignTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [pendingTemplate, setPendingTemplate] = useState<CampaignTemplate | null>(null);
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  // SDA-192: test-send-to-self state + result notice (success or fallback flag).
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testSendNotice, setTestSendNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const variableMenuRef = useRef<HTMLDivElement>(null);
  // SDA-188: monotonic request counter so an out-of-order audience response can
  // never overwrite the count from a newer request.
  const audienceSeqRef = useRef(0);

  // Delivery metrics + failed sends (for sent/sending campaigns)
  const [failedSends, setFailedSends] = useState<EmailSendRecord[]>([]);
  const [showFailedSends, setShowFailedSends] = useState(false);
  const [unsubscribedIds, setUnsubscribedIds] = useState<Set<string>>(new Set());
  const [unsubAllLoading, setUnsubAllLoading] = useState(false);

  // Tour cities for the selected show — used to detect city/show mismatches (SDA-168)
  const [showTourCities, setShowTourCities] = useState<City[] | null>(null);

  useEffect(() => {
    if (isEditing && id) {
      loadCampaign(id);
    }
  }, [id, isEditing]);

  // Load failed sends when viewing a sent/sending campaign
  useEffect(() => {
    if (id && (campaignStatus === 'sent' || campaignStatus === 'sending') && campaign?.metrics?.failed && campaign.metrics.failed > 0) {
      getCampaignSends(id, { status: 'failed', perPage: 100 })
        .then(res => setFailedSends(res.data))
        .catch(() => {});
    }
  }, [id, campaignStatus, campaign?.metrics?.failed]);

  useEffect(() => {
    getShows().then(setShows).catch(() => {});
    getCities().then(setCities).catch(() => {});
    // Load templates on mount so auto-fill works immediately
    getCampaignTemplates()
      .then((result) => setTemplates(Array.isArray(result.templates) ? result.templates : []))
      .catch(() => {});
  }, []);


  // Auto-fetch audience when campaign is saved (isEditing) and targeting changes.
  // SDA-188: wait for initialLoadComplete so the first fetch uses the saved
  // targeting, not the uninitialized empty arrays.
  useEffect(() => {
    if (!isEditing || !id || !initialLoadComplete) return;
    fetchAudience();
  }, [id, isEditing, initialLoadComplete, selectedShowIds, selectedCityIds, excludeBooked, includeInterested]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAudience = async (showAll = false) => {
    if (!id) return;
    // SDA-188: tag this request; only the latest one is allowed to write state.
    const seq = ++audienceSeqRef.current;
    setAudienceLoading(true);
    try {
      const result = await getCampaignAudience(id, showAll, {
        show_ids: selectedShowIds,
        city_ids: selectedCityIds,
        exclude_booked: excludeBooked,
        include_interested: includeInterested,
      });
      if (seq === audienceSeqRef.current) setAudience(result);
    } catch {
      // silent
    } finally {
      if (seq === audienceSeqRef.current) setAudienceLoading(false);
    }
  };

  // Derived: show campaigns (tour_announcement / reminder) require exactly 1 show + 1 city.
  // Used to disable Send / Schedule / Save & Continue and show inline helper text.
  const isShowCampaign = campaignType === 'tour_announcement' || campaignType === 'reminder';
  const showCampaignMissingTargeting =
    isShowCampaign && (selectedShowIds.length !== 1 || selectedCityIds.length !== 1);

  // Fetch distinct tour cities for the single selected show so we can warn when
  // the selected city isn't on the show's tour (SDA-168). Clear whenever the
  // condition isn't met so state doesn't linger from a prior selection.
  useEffect(() => {
    if (!isShowCampaign || selectedShowIds.length !== 1 || !selectedShowIds[0]) {
      setShowTourCities(null);
      return;
    }
    const showId = selectedShowIds[0];
    const controller = new AbortController();

    getAllTourStops({ show_id: showId })
      .then(({ tour_stops }) => {
        if (controller.signal.aborted) return;
        // Keep only upcoming stops (end_date >= today, UTC) — matching the
        // audience build on the backend so the mismatch notice fires correctly.
        const todayStr = new Date().toISOString().substring(0, 10);
        const upcoming = tour_stops.filter((s) => s.end_date >= todayStr);
        // Dedupe by city id — each unique city appears once
        const seen = new Set<string>();
        const distinct: City[] = [];
        for (const stop of upcoming) {
          if (stop.city && !seen.has(stop.city.id)) {
            seen.add(stop.city.id);
            distinct.push(stop.city as City);
          }
        }
        setShowTourCities(distinct);
      })
      .catch(() => {
        if (!controller.signal.aborted) setShowTourCities([]);
      });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowCampaign, selectedShowIds[0]]);

  // SDA-168: city mismatch notice — true only when all conditions hold:
  // single show campaign, single city selected, tour cities have loaded with at
  // least one stop, and the selected city is not among them.
  // Guards showTourCities.length > 0 so the empty-tour-stops path stays distinct.
  const cityNotOnShowTour =
    isShowCampaign &&
    selectedShowIds.length === 1 &&
    selectedCityIds.length === 1 &&
    showTourCities !== null &&
    showTourCities.length > 0 &&
    !showTourCities.some((c) => c.id === selectedCityIds[0]);

  // City mismatch notice: suggested tour cities ordered same-state-first, capped at 5
  const cityMismatchSuggestions = useMemo(() => {
    if (!cityNotOnShowTour || !showTourCities) return [];
    const selectedCity = cities.find((c) => c.id === selectedCityIds[0]);
    const sameState = showTourCities.filter((c) => c.state === selectedCity?.state);
    const otherStates = showTourCities.filter((c) => c.state !== selectedCity?.state);
    return [...sameState, ...otherStates].slice(0, 5);
  }, [cityNotOnShowTour, showTourCities, selectedCityIds, cities]);

  const cityMismatchRemainder =
    showTourCities && cityNotOnShowTour ? Math.max(0, showTourCities.length - 5) : 0;

  // Variables used in subject+body that will render EMPTY given the current
  // targeting — so we can tell the admin specifically, while editing.
  const emptyVars = useMemo(() => {
    const tokens = [...new Set(
      (`${subject} ${previewText} ${htmlContent}`.match(/\{\{(\w+)\}\}/g) ?? []).map((m) => m.slice(2, -2)),
    )];
    return emptyVariablesForTargeting(tokens, {
      hasShow: selectedShowIds.length > 0,
      singleCity: selectedShowIds.length === 0 && selectedCityIds.length === 1,
    });
  }, [subject, previewText, htmlContent, selectedShowIds, selectedCityIds]);


  // Auto-fill template when campaign type changes on a new (unsaved) campaign
  useEffect(() => {
    if (isEditing || !templates?.length) return;
    const match = templates.find((t) => t.campaign_type === campaignType);
    if (!match) return;
    const hasContent = htmlContent.replace(/<[^>]*>/g, '').trim();
    if (hasContent) return; // don't overwrite content the user already typed
    setSubject(match.subject);
    setHtmlContent(match.html_content);
  }, [campaignType, templates]);

  const loadCampaign = async (campaignId: string) => {
    try {
      setIsLoading(true);
      const campaign = await getCampaign(campaignId);
      setCampaign(campaign);
      setName(campaign.name);
      setSubject(campaign.subject);
      setPreviewText(campaign.preview_text || '');
      setHtmlContent(campaign.html_content || '');
      setPlainTextContent(campaign.plain_text_content || '');
      setCampaignType(campaign.campaign_type);
      setCampaignStatus(campaign.status || 'draft');
      setSelectedShowIds(
        Array.isArray(campaign.target_show_ids) && campaign.target_show_ids.length > 0
          ? campaign.target_show_ids
          : campaign.target_show_id ? [campaign.target_show_id] : []
      );
      setSelectedCityIds(
        Array.isArray(campaign.target_city_ids) && campaign.target_city_ids.length > 0
          ? campaign.target_city_ids
          : campaign.target_city_id ? [campaign.target_city_id] : []
      );
      setExcludeBooked(campaign.exclude_booked ?? false);
      setIncludeInterested(campaign.include_interested ?? false);
      if (campaign.scheduled_for) {
        // Use stored timezone or detect from browser
        const tz =
          (campaign.scheduled_timezone as USTimezone) || detectUSTimezone();
        setScheduledTimezone(tz);
        // Convert UTC back to the naive datetime in the stored timezone
        setScheduledFor(utcToNaiveDatetime(campaign.scheduled_for, tz));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load campaign');
    } finally {
      setIsLoading(false);
      // SDA-188: targeting state is now set — allow the audience effect to run.
      setInitialLoadComplete(true);
    }
  };

  // Close variable menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (variableMenuRef.current && !variableMenuRef.current.contains(e.target as Node)) {
        setShowVariableMenu(false);
      }
    };
    if (showVariableMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVariableMenu]);

  const handleInsertVariable = useCallback((variable: string) => {
    setHtmlContent((prev) => prev + variable);
    setShowVariableMenu(false);
  }, []);

  const handleLoadTemplates = async () => {
    setShowTemplateModal(true);
    if (templates.length > 0) return;
    setTemplatesLoading(true);
    try {
      const result = await getCampaignTemplates();
      setTemplates(result.templates);
    } catch {
      // templates may not be available yet
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleUseTemplate = (template: CampaignTemplate) => {
    const hasContent = subject.trim() || htmlContent.replace(/<[^>]*>/g, '').trim();
    if (hasContent) {
      setPendingTemplate(template);
      return;
    }
    applyTemplate(template);
  };

  const applyTemplate = (template: CampaignTemplate) => {
    setSubject(template.subject);
    setHtmlContent(template.html_content);
    setShowTemplateModal(false);
    setPendingTemplate(null);
  };

  // SDA-192: send a test copy of the saved campaign to the logged-in admin's own
  // inbox so they can see exactly what lands before approving. Targets ONLY the
  // admin (server-enforced). Surfaces any friendly-fallback fields in the notice.
  const handleSendTest = async () => {
    if (!id) return;
    setError(null);
    setTestSendNotice(null);
    try {
      setIsSendingTest(true);
      const res = await sendTestCampaignEmail(id);
      const fb = res.fallbacks_applied ?? [];
      const fbNote = fb.length > 0
        ? ` Note — these fields fell back to a default: ${fb.map((f) => `${f.token} → ${f.rendered_as}`).join(', ')}.`
        : '';
      setTestSendNotice({ ok: true, text: `Test sent to ${res.sent_to}. Check your inbox.${fbNote}` });
    } catch (err) {
      setTestSendNotice({ ok: false, text: err instanceof Error ? err.message : 'Failed to send test email' });
    } finally {
      setIsSendingTest(false);
    }
  };

  const handlePreview = async () => {
    setShowPreviewModal(true);

    // Always preview the CURRENT editor content, not the saved DB version
    let resolvedVars: Record<string, string> = {};

    // If campaign is saved, fetch resolved variables from backend (show data, pricing, dates, etc.)
    if (id) {
      setPreviewLoading(true);
      try {
        const result = await resolveCampaignTemplate(id);
        if (result.variables_available) {
          resolvedVars = result.variables_available as Record<string, string>;
          setResolvedVariables(resolvedVars);
        }
      } catch {
        // Fallback to sample values only
      } finally {
        setPreviewLoading(false);
      }
    }

    // Override sample values with currently selected show/city data
    const formOverrides: Record<string, string> = {};
    if (selectedShowIds.length > 0) {
      const firstShow = shows.find(s => s.id === selectedShowIds[0]);
      if (firstShow) {
        formOverrides.show_name = firstShow.name;
      }
    }
    // Only fill {{city}}/{{state}} from a targeted city when EXACTLY one city is
    // selected (matches the send — a multi-city campaign can't have one city).
    if (selectedShowIds.length === 0 && selectedCityIds.length === 1) {
      const onlyCity = cities.find(c => c.id === selectedCityIds[0]);
      if (onlyCity) {
        formOverrides.city = onlyCity.name;
        formOverrides.state = onlyCity.state;
      }
    }

    // Filter out unresolved API values (still contain {{ or are empty)
    const cleanResolvedVars: Record<string, string> = {};
    for (const [k, v] of Object.entries(resolvedVars)) {
      if (v && !v.includes('{{')) cleanResolvedVars[k] = v;
    }

    // Substitute variables in the CURRENT editor content (not DB content)
    // Priority: form selections (highest) > backend resolved > sample defaults (lowest)
    const allVars = { ...PREVIEW_SAMPLE_VALUES, ...cleanResolvedVars, ...formOverrides };
    const finalHtml = htmlContent.replace(/\{\{(\w+)\}\}/g,
      (_m, k: string) => allVars[k] ?? `{{${k}}}`
    );
    setPreviewHtml(finalHtml);

    const substSubject = subject.replace(/\{\{(\w+)\}\}/g,
      (_m, k: string) => allVars[k] ?? `{{${k}}}`
    );
    setResolvedSubject(substSubject);
  };

  // Compute the UTC ISO string from the naive datetime + selected timezone
  const scheduledUTC = useMemo(() => {
    if (!scheduledFor) return null;
    try {
      return naiveDatetimeToUTC(scheduledFor, scheduledTimezone);
    } catch {
      return null;
    }
  }, [scheduledFor, scheduledTimezone]);

  // SDA-222: a queued (pending_approval) campaign whose currently-entered send
  // date is a past calendar day (UTC) — the exact condition that blocks Approve
  // All & Schedule (mirrors the backend chain-approve guard, date-level UTC).
  // Drives an upfront warning banner so the user knows BEFORE hitting Save, and
  // clears the moment they pick a future date.
  const pendingApprovalPastDate = useMemo(() => {
    if (campaignStatus !== 'pending_approval' || !scheduledUTC) return false;
    const todayUtc = new Date().toISOString().split('T')[0]!;
    return scheduledUTC.split('T')[0]! < todayUtc;
  }, [campaignStatus, scheduledUTC]);

  // Scroll the top-of-form error banner into view whenever an error is set — the
  // submit button sits at the bottom of a long form, so a refusal shown at the
  // top would otherwise be off-screen and easy to miss.
  useEffect(() => {
    if (error) {
      errorBannerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  // Preview times in all other timezones
  const otherTimezones = useMemo(() => {
    if (!scheduledUTC) return [];
    return US_TIMEZONES.filter((tz) => tz.value !== scheduledTimezone).map(
      (tz) => ({
        ...tz,
        display: formatInTimezone(scheduledUTC, tz.value),
      })
    );
  }, [scheduledUTC, scheduledTimezone]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Please enter a campaign name');
      return;
    }
    if (!subject.trim()) {
      setError('Please enter an email subject');
      return;
    }
    const strippedContent = htmlContent.replace(/<[^>]*>/g, '').trim();
    if (!strippedContent) {
      setError('Please enter email content');
      return;
    }
    // SDA-194: a show campaign must keep exactly one show + one city. Block the
    // save up front with a friendly message instead of letting the backend 400.
    if (isShowCampaign && showCampaignMissingTargeting) {
      setError('A show campaign must target exactly one show and one city.');
      return;
    }

    // SDA-201: a queued (pending_approval) campaign must keep a valid future
    // send date so it stays sendable and keeps its inline reschedule control in
    // the Queue. Block clearing the date or saving an overdue one with a
    // friendly message (the backend also guards past dates with a raw 400).
    if (campaignStatus === 'pending_approval') {
      if (!scheduledFor || !scheduledUTC) {
        setError('A queued campaign needs a send date.');
        return;
      }
      if (new Date(scheduledUTC) <= new Date()) {
        setError('Choose a send date in the future.');
        return;
      }
    }

    // Show the "schedule this?" confirmation only for a true draft → scheduled
    // action (new campaigns also default to 'draft'). A pending_approval save
    // is NOT a send — it stays queued and still needs Approve All & Schedule —
    // so it skips the confirm. 'scheduled' edits skip it too.
    if (scheduledFor && scheduledUTC && campaignStatus === 'draft' && !showScheduleConfirm) {
      setShowScheduleConfirm(true);
      return;
    }
    setShowScheduleConfirm(false);

    try {
      setIsSaving(true);

      if (isEditing && id) {
        const updated = await updateCampaign(id, {
          name: name.trim(),
          subject: subject.trim(),
          preview_text: previewText.trim() || undefined,
          html_content: htmlContent,
          plain_text_content: plainTextContent.trim() || undefined,
          scheduled_for: scheduledUTC || null,
          scheduled_timezone: scheduledFor ? scheduledTimezone : null,
          // SDA-194: always send the arrays (even empty) so the backend can tell
          // "cleared" from "unchanged" — an empty array clears targeting; omitting
          // the field would silently preserve the old targeting.
          show_ids: selectedShowIds,
          city_ids: selectedCityIds,
          exclude_booked: excludeBooked,
          include_interested: includeInterested,
        });
        // SDA-194: keep local campaign state in sync with the targeting we just
        // saved (the SDA-192 no-reload save would otherwise leave it stale).
        setCampaign((prev) => (prev ? {
          ...prev,
          status: updated.status,
          scheduled_for: updated.scheduled_for,
          target_show_ids: selectedShowIds,
          target_city_ids: selectedCityIds,
          target_show_id: selectedShowIds[0] ?? null,
          target_city_id: selectedCityIds[0] ?? null,
        } : updated));
      } else {
        const newCampaign = await createCampaign({
          name: name.trim(),
          subject: subject.trim(),
          preview_text: previewText.trim() || undefined,
          html_content: htmlContent,
          plain_text_content: plainTextContent.trim() || undefined,
          campaign_type: campaignType,
          scheduled_for: scheduledUTC || undefined,
          scheduled_timezone: scheduledFor ? scheduledTimezone : undefined,
          // SDA-194: always send the arrays (even empty) so the backend can tell
          // "cleared" from "unchanged" — an empty array clears targeting; omitting
          // the field would silently preserve the old targeting.
          show_ids: selectedShowIds,
          city_ids: selectedCityIds,
          exclude_booked: excludeBooked,
          include_interested: includeInterested,
        });
        // Stay on edit page so user can see audience preview and email preview
        navigate(`/admin/campaigns/${newCampaign.id}`);
        return;
      }
      // SDA-201: a saved queued campaign stays pending_approval (still in the
      // Queue) — send the admin back to the Queue, not the Scheduled tab where
      // it wouldn't appear.
      if (campaignStatus === 'pending_approval') {
        navigate('/admin/campaigns?tab=queue');
        return;
      }
      // If campaign was just scheduled, redirect to campaigns list
      if (scheduledUTC) {
        navigate('/admin/campaigns?tab=scheduled');
        return;
      }
      // For non-scheduled edits, stay on page
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save campaign');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStopCampaign = async () => {
    if (!id) return;
    try {
      const result = await stopCampaign(id);
      setCampaign(prev => prev ? { ...prev, status: 'cancelled', sent_count: result.sent_count, completed_at: new Date().toISOString() } : prev);
      setCampaignStatus('cancelled');
      setShowStopConfirm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop campaign');
      setShowStopConfirm(false);
    }
  };

  const handleSendNow = async () => {
    if (!id) return;
    if (!name.trim()) { setError('Please enter a campaign name'); return; }
    try {
      setIsSending(true);
      setError(null);
      setShowSendConfirm(false);
      setCampaignStatus('sending');

      // Auto-save current form state so Send Now sends what the user sees,
      // not whatever was last persisted via Save Draft. Mirrors the
      // updateCampaign payload used by handleSaveDraft + the Schedule path
      // but intentionally omits scheduled_for / scheduled_timezone so the
      // existing schedule fields aren't mutated as a side effect.
      await updateCampaign(id, {
        name: name.trim(),
        subject: subject.trim() || 'Draft',
        preview_text: previewText.trim() || undefined,
        html_content: htmlContent || '<p></p>',
        plain_text_content: plainTextContent.trim() || undefined,
        // SDA-194: send arrays verbatim (see save handlers).
        show_ids: selectedShowIds,
        city_ids: selectedCityIds,
        exclude_booked: excludeBooked,
        include_interested: includeInterested,
      });

      await sendCampaign(id, true);
      // Reload campaign to get fresh metrics
      const updated = await getCampaign(id);
      setCampaign(updated);
      setCampaignStatus(updated.status || 'sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send campaign');
      // Reload to get actual status
      if (id) {
        try {
          const updated = await getCampaign(id);
          setCampaign(updated);
          setCampaignStatus(updated.status || 'draft');
        } catch { /* ignore */ }
      }
    } finally {
      setIsSending(false);
    }
  };

  const [pendingChainCancel, setPendingChainCancel] = useState(false);

  const handleSaveDraft = async () => {
    if (!name.trim()) { setError('Please enter a campaign name'); return; }
    // SDA-194: block clearing a show campaign's targeting (must stay 1 show + 1 city).
    if (isShowCampaign && showCampaignMissingTargeting) {
      setError('A show campaign must target exactly one show and one city.');
      return;
    }

    // If this is a scheduled announcement in a chain, ask about cancelling reminders too
    if (isEditing && campaign?.tour_stop_id && campaign?.sequence_number === 1
        && campaignStatus === 'scheduled' && !pendingChainCancel) {
      setPendingChainCancel(true);
      return;
    }
    setPendingChainCancel(false);

    setError(null);
    try {
      setIsSaving(true);
      if (isEditing && id) {
        const updated = await updateCampaign(id, {
          name: name.trim(),
          subject: subject.trim() || 'Draft',
          preview_text: previewText.trim() || undefined,
          html_content: htmlContent || '<p></p>',
          plain_text_content: plainTextContent.trim() || undefined,
          scheduled_for: null,
          // SDA-194: always send the arrays (even empty) so the backend can tell
          // "cleared" from "unchanged" — an empty array clears targeting; omitting
          // the field would silently preserve the old targeting.
          show_ids: selectedShowIds,
          city_ids: selectedCityIds,
          exclude_booked: excludeBooked,
          include_interested: includeInterested,
        });
        // SDA-190 + SDA-192: refresh ONLY the status/schedule the ACTIONS panel
        // needs, from the row updateCampaign returns — do NOT re-load the whole
        // campaign (which called setHtmlContent(dbValue) and, via the editor's
        // value-sync, could clobber the content the user just typed). Merge into
        // the existing campaign so enriched fields the PUT row omits (metrics,
        // quota, resume_after) are preserved. Every editor field already matches
        // what we just saved.
        // SDA-194: also sync the just-saved targeting into local state so a
        // cleared/changed show/city set isn't left stale by the no-reload save.
        setCampaign((prev) => (prev ? {
          ...prev,
          status: updated.status,
          scheduled_for: updated.scheduled_for,
          target_show_ids: selectedShowIds,
          target_city_ids: selectedCityIds,
          target_show_id: selectedShowIds[0] ?? null,
          target_city_id: selectedCityIds[0] ?? null,
        } : updated));
        setCampaignStatus(updated.status || 'draft');
        setScheduledFor('');
      } else {
        const draftCampaign = await createCampaign({
          name: name.trim(),
          subject: subject.trim() || 'Draft',
          html_content: htmlContent || '<p></p>',
          campaign_type: campaignType,
          // SDA-194: always send the arrays (even empty) so the backend can tell
          // "cleared" from "unchanged" — an empty array clears targeting; omitting
          // the field would silently preserve the old targeting.
          show_ids: selectedShowIds,
          city_ids: selectedCityIds,
          exclude_booked: excludeBooked,
          include_interested: includeInterested,
        });
        navigate(`/admin/campaigns/${draftCampaign.id}`);
        return;
      }
      // For editing, stay on page
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendClick = async () => {
    const allTokens = [...new Set(
      (`${subject} ${previewText} ${htmlContent}`.match(/\{\{(\w+)\}\}/g) ?? []).map((m) => m.slice(2, -2)),
    )];
    const perRecipientSet = new Set<string>(PER_RECIPIENT_VARS);

    // Only the variables that will actually render EMPTY given the targeting
    // (same logic as the live editor warning) — not every non-per-recipient token.
    const unresolvedShowVars = emptyVars.map((name) => `{{${name}}}`);
    const perRecipientVars = allTokens
      .filter((name) => perRecipientSet.has(name))
      .map((name) => `{{${name}}}`);

    setUnresolvedShowVars(unresolvedShowVars);
    setPerRecipientVarsState(perRecipientVars);
    setPendingSend(true);
    await handlePreview();
  };

  if (isLoading) {
    return (
      <div>
        <div className="h-10 bg-warm-200 rounded w-1/4 mb-8 animate-pulse" />
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="space-y-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 bg-warm-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const selectedTzInfo = US_TIMEZONES.find(
    (tz) => tz.value === scheduledTimezone
  )!;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Link
          to="/admin/campaigns"
          className="p-2 text-warm-500 hover:text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">
          {isEditing ? 'Edit Campaign' : 'New Campaign'}
        </h1>
        {campaignStatus === 'pending_approval' && (
          <span className="px-2.5 py-1 text-xs font-semibold bg-amber-100 text-amber-800 rounded-full">
            Pending Approval
          </span>
        )}
        <div className="ml-auto flex items-center gap-3">
          {isEditing && (
            <button
              type="button"
              onClick={handleLoadTemplates}
              className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors text-sm font-medium"
            >
              Change Template
            </button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Auto-generated campaign banner */}
            {campaign?.auto_generated && (
              <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 flex items-start gap-3">
                <svg className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-primary-900">
                    Auto-generated {campaign.campaign_type === 'reminder' ? 'reminder' : 'announcement'} campaign
                  </p>
                  {campaign.parent_campaign_id && (
                    <p className="text-xs text-primary-700 mt-0.5">
                      Part of a campaign chain &middot; Sequence #{campaign.sequence_number}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Parent campaign link */}
            {campaign?.parent_campaign_id && (
              <div>
                <Link
                  to={`/admin/campaigns/${campaign.parent_campaign_id}/edit`}
                  className="text-sm text-primary-600 hover:text-primary-700 hover:underline inline-flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  View parent announcement
                </Link>
              </div>
            )}

            {error && (
              <div ref={errorBannerRef} className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
                {error}
              </div>
            )}

            {/* SDA-222: upfront past-date warning for a queued campaign, so the
                user re-dates BEFORE hitting Save (which the backend would refuse). */}
            {pendingApprovalPastDate && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>This send date has passed — choose a future date to save and approve this campaign.</span>
              </div>
            )}

            {/* Step 1: Choose a Template */}
            <div className="flex items-center gap-3 pb-3 border-b border-warm-200">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">1</div>
              <div>
                <h2 className="text-sm font-semibold text-warm-900">Step 1 of 4: Choose a Template</h2>
                <p className="text-xs text-warm-500">Pick a pre-written email template or start from scratch</p>
              </div>
            </div>

            {/* Inline Template Picker — shown for new campaigns */}
            {!isEditing && templates.length > 0 && (() => {
              const searchLower = templateSearch.trim().toLowerCase();
              const filtered = searchLower
                ? templates.filter((t) =>
                    (t.name ?? '').toLowerCase().includes(searchLower) ||
                    (t.description ?? '').toLowerCase().includes(searchLower)
                  )
                : templates;
              const systemTemplates = filtered.filter((t) => t.is_system_template !== false);
              const customTemplates = filtered.filter((t) => t.is_system_template === false);

              const renderRow = (t: CampaignTemplate) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { handleUseTemplate(t); setCampaignType(t.campaign_type); }}
                  className="w-full text-left bg-white border border-warm-200 hover:border-primary-400 hover:bg-primary-50/30 rounded-lg px-4 py-3 transition-colors flex items-center gap-3 group"
                >
                  <span className="inline-block text-xs font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full shrink-0">
                    {t.campaign_type.replace('_', ' ')}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-warm-900 group-hover:text-primary-700 truncate">{t.name}</p>
                    {t.description && (
                      <p className="text-xs text-warm-500 truncate mt-0.5">{t.description}</p>
                    )}
                  </div>
                  {!t.is_system_template && (
                    <span className="text-[10px] font-semibold text-warm-600 bg-warm-100 px-1.5 py-0.5 rounded uppercase tracking-wide shrink-0">
                      Custom
                    </span>
                  )}
                  <span className="text-xs text-primary-500 font-medium group-hover:text-primary-700 shrink-0">
                    Use →
                  </span>
                </button>
              );

              if (!templateSelectorOpen) {
                return (
                  <button
                    type="button"
                    onClick={() => setTemplateSelectorOpen(true)}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
                  >
                    Show template picker ▼
                  </button>
                );
              }

              return (
                <div className="bg-white border-2 border-primary-100 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <div>
                      <p className="text-sm font-semibold text-primary-800">Start with a template</p>
                      <p className="text-xs text-primary-600 mt-0.5">Pre-filled with the full Hamilton-style email — just select your show and send.</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        type="button"
                        onClick={() => setTemplateSelectorOpen(false)}
                        className="text-xs text-warm-500 hover:text-warm-700 font-medium"
                      >
                        Hide ▲
                      </button>
                      <button
                        type="button"
                        onClick={handleLoadTemplates}
                        className="text-xs text-primary-600 hover:underline"
                      >
                        All templates →
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
                      <input
                        type="text"
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                        placeholder="Search templates by name or description…"
                        className="w-full pl-9 pr-3 py-2 border border-warm-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {filtered.length === 0 ? (
                    <div className="text-center py-6 text-sm text-warm-500">No templates match your search.</div>
                  ) : (
                    <>
                      {systemTemplates.length > 0 && (
                        <>
                          <p className="text-xs font-semibold text-warm-700 uppercase tracking-wide mb-2">System Templates</p>
                          <div className="space-y-2 mb-5">
                            {systemTemplates.map(renderRow)}
                          </div>
                        </>
                      )}
                      {customTemplates.length > 0 && (
                        <>
                          <p className="text-xs font-semibold text-warm-700 uppercase tracking-wide mb-2 mt-4">Custom Templates</p>
                          <div className="space-y-2">
                            {customTemplates.map(renderRow)}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            {/* Campaign Details */}
            <div className="bg-white rounded-xl shadow-sm border border-warm-100 p-6">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-0.5 h-5 bg-primary-500 rounded-full flex-shrink-0" />
                <h3 className="text-sm font-semibold text-warm-900 uppercase tracking-wide">Campaign Details</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., Hamilton NYC Tour Announcement"
                    required
                  />
                  <p className="text-xs text-warm-500 mt-1">Internal name, not shown to recipients</p>
                </div>

                {!isEditing && (
                  <div>
                    <label className="block text-sm font-medium text-warm-700 mb-1">
                      Campaign Type
                    </label>
                    <select
                      value={campaignType}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setCampaignType(newType);
                        if (newType === 'activation' || newType === 'faculty_invitation' || campaignType === 'activation' || campaignType === 'faculty_invitation') {
                          setSelectedShowIds([]);
                          setSelectedCityIds([]);
                          setExcludeBooked(false);
                          setIncludeInterested(false);
                        }
                      }}
                      className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {CAMPAIGN_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: Set Targeting */}
            <div className="flex items-center gap-3 pb-3 border-b border-warm-200">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">2</div>
              <div>
                <h2 className="text-sm font-semibold text-warm-900">Step 2 of 4: Set Targeting</h2>
                <p className="text-xs text-warm-500">
                  {campaignType === 'activation'
                    ? 'Activation campaigns automatically target dormant studios'
                    : campaignType === 'faculty_invitation'
                    ? 'Faculty invitation campaigns automatically target all invited faculty'
                    : 'Choose which shows and cities to target'}
                </p>
              </div>
            </div>

            {campaignType === 'activation' && (
              <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-primary-800">Activation Campaign</p>
                <p className="text-xs text-primary-600 mt-1">
                  This campaign will automatically be sent to all studios that haven't activated their accounts yet (dormant studios).
                  No targeting needed — the audience is determined automatically.
                </p>
              </div>
            )}

            {campaignType === 'faculty_invitation' && (
              <div className="bg-primary-50 border border-primary-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-primary-800">Faculty Invitation Campaign</p>
                <p className="text-xs text-primary-600 mt-1">
                  Recipients: All invited faculty (auto-populated). No manual targeting needed — the audience is determined automatically from faculty invitations.
                </p>
              </div>
            )}

            {/* Targeting */}
            {campaignType !== 'activation' && campaignType !== 'faculty_invitation' && (
            <div className="bg-white rounded-xl shadow-sm border border-warm-100 p-6">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-0.5 h-5 bg-primary-500 rounded-full flex-shrink-0" />
                <h3 className="text-sm font-semibold text-warm-900 uppercase tracking-wide">Targeting</h3>
              </div>
              <p className="text-xs text-warm-500 mb-4">
                {campaignType === 'tour_announcement' || campaignType === 'reminder'
                  ? 'Show campaigns require exactly one show and one city.'
                  : 'Optionally limit to a specific city. Leave blank to send to all opted-in studios.'}
              </p>

              <div className="space-y-4">
                {/* Target Show — single-select (selecting a new show replaces the current one) */}
                {(campaignType === 'tour_announcement' || campaignType === 'reminder') && (
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Target Show <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={showSearch}
                    onChange={(e) => setShowSearch(e.target.value)}
                    placeholder="Search shows..."
                    className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {showSearch && (
                    <div className="mt-1 border border-warm-200 rounded-lg max-h-40 overflow-y-auto bg-white shadow-sm">
                      {shows
                        .filter((s) => s.name.toLowerCase().includes(showSearch.toLowerCase()))
                        .map((show) => (
                          <button
                            key={show.id}
                            type="button"
                            onClick={() => {
                              // Single-select: replace any existing selection
                              setSelectedShowIds([show.id]);
                              setShowSearch('');
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-warm-50 transition-colors border-b border-warm-100 last:border-0"
                          >
                            {show.name}
                          </button>
                        ))}
                      {shows.filter((s) => s.name.toLowerCase().includes(showSearch.toLowerCase())).length === 0 && (
                        <p className="px-4 py-2 text-sm text-warm-400">No matching shows</p>
                      )}
                    </div>
                  )}
                  {selectedShowIds.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedShowIds.map((showId) => {
                        const show = shows.find((s) => s.id === showId);
                        return (
                          <span
                            key={showId}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                          >
                            {show?.name || showId}
                            <button
                              type="button"
                              onClick={() => setSelectedShowIds([])}
                              className="text-primary-500 hover:text-primary-700"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                )}

                {/* Target City — single-select (selecting a new city replaces the current one) */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Target City
                    {(campaignType === 'tour_announcement' || campaignType === 'reminder') && (
                      <span className="text-danger-500"> *</span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    placeholder="Search cities..."
                    className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {citySearch && (
                    <div className="mt-1 border border-warm-200 rounded-lg max-h-40 overflow-y-auto bg-white shadow-sm">
                      {cities
                        .filter(
                          (c) =>
                            `${c.name}, ${c.state}`.toLowerCase().includes(citySearch.toLowerCase())
                        )
                        .map((city) => (
                          <button
                            key={city.id}
                            type="button"
                            onClick={() => {
                              // Single-select: replace any existing selection
                              setSelectedCityIds([city.id]);
                              setCitySearch('');
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-warm-50 transition-colors border-b border-warm-100 last:border-0"
                          >
                            {city.name}, {city.state}
                          </button>
                        ))}
                      {cities.filter(
                        (c) =>
                          `${c.name}, ${c.state}`.toLowerCase().includes(citySearch.toLowerCase())
                      ).length === 0 && (
                        <p className="px-4 py-2 text-sm text-warm-400">No matching cities</p>
                      )}
                    </div>
                  )}
                  {selectedCityIds.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedCityIds.map((cityId) => {
                        const city = cities.find((c) => c.id === cityId);
                        return (
                          <span
                            key={cityId}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm"
                          >
                            {city ? `${city.name}, ${city.state}` : cityId}
                            <button
                              type="button"
                              onClick={() => setSelectedCityIds([])}
                              className="text-primary-500 hover:text-primary-700"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* SDA-168: city/show tour mismatch notice */}
                {isShowCampaign && selectedShowIds.length === 1 && selectedCityIds.length === 1 && showTourCities !== null && (
                  <>
                    {showTourCities.length === 0 ? (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs">
                        <p className="font-semibold text-amber-800 flex items-center gap-1">
                          <span>⚠</span>{' '}
                          {shows.find((s) => s.id === selectedShowIds[0])?.name ?? 'This show'} has no upcoming tour stops to announce yet.
                        </p>
                      </div>
                    ) : cityNotOnShowTour ? (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs">
                        <p className="font-semibold text-amber-800 mb-1 flex items-center gap-1">
                          <span>⚠</span>{' '}
                          {shows.find((s) => s.id === selectedShowIds[0])?.name ?? 'This show'} doesn&apos;t tour{' '}
                          {(() => { const c = cities.find((x) => x.id === selectedCityIds[0]); return c ? `${c.name}, ${c.state}` : 'that city'; })()}, so this campaign has no recipients.
                        </p>
                        <p className="text-amber-700 mb-1">
                          {shows.find((s) => s.id === selectedShowIds[0])?.name ?? 'This show'} tours:{' '}
                          {cityMismatchSuggestions.map((c) => `${c.name}, ${c.state}`).join(' · ')}
                          {cityMismatchRemainder > 0 && ` +${cityMismatchRemainder} more`}
                          {' '}— pick one of those.
                        </p>
                        <p className="text-amber-600">Studios near that market are still included automatically.</p>
                      </div>
                    ) : null}
                  </>
                )}

                {/* SDA-168 follow-up: inline city-reach line */}
                {isEditing && (audienceLoading || (audience?.audience_cities && audience.audience_cities.length > 0)) && (
                  <div className="text-xs text-warm-600">
                    {audienceLoading ? (
                      <span className="italic text-warm-400">Calculating which cities this will reach…</span>
                    ) : audience?.audience_cities && audience.audience_cities.length > 0 ? (
                      <details>
                        <summary className="cursor-pointer list-none flex items-center gap-1 select-none">
                          <span className="text-warm-400 text-[10px]">&#9654;</span>
                          <span>
                            Reaches studios in{' '}
                            {audience.audience_cities.slice(0, 3).map(c => `${c.city}, ${c.state}`).join(' · ')}
                            {audience.audience_cities.length > 3 && ` +${audience.audience_cities.length - 3} more`}
                          </span>
                        </summary>
                        <ul className="mt-1.5 ml-3 space-y-0.5">
                          {audience.audience_cities.map(c => {
                            // Targeted cities are the campaign's own market(s): "tour city"
                            // for a show campaign, "selected" for a city-only campaign.
                            // Everything else was pulled in by proximity → "nearby".
                            const label = c.is_target
                              ? (selectedShowIds.length > 0 ? 'tour city' : 'selected')
                              : 'nearby';
                            return (
                              <li key={`${c.city}-${c.state}`} className="text-warm-500">
                                {c.city}, {c.state} — {c.count}{' '}
                                <span className={c.is_target ? 'text-primary-600' : 'text-warm-400'}>
                                  ({label})
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </details>
                    ) : null}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="excludeBooked"
                    checked={excludeBooked}
                    onChange={(e) => setExcludeBooked(e.target.checked)}
                    disabled={selectedShowIds.length === 0}
                    className="h-4 w-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                  />
                  <label
                    htmlFor="excludeBooked"
                    className={`text-sm font-medium ${selectedShowIds.length > 0 ? 'text-warm-700' : 'text-warm-400'}`}
                  >
                    Exclude studios with existing bookings
                  </label>
                </div>

                {selectedShowIds.length > 0 && (
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="includeInterested"
                      checked={includeInterested}
                      onChange={(e) => setIncludeInterested(e.target.checked)}
                      className="h-4 w-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                    />
                    <label htmlFor="includeInterested" className="text-sm font-medium text-warm-700">
                      Also include studios who expressed interest in these shows
                    </label>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Step 3: Write Your Email */}
            <div className="flex items-center gap-3 pb-3 border-b border-warm-200">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">3</div>
              <div>
                <h2 className="text-sm font-semibold text-warm-900">Step 3 of 4: Write Your Email</h2>
                <p className="text-xs text-warm-500">Customize the email content and subject line</p>
              </div>
            </div>

            {/* Email Content + Live Preview split layout */}
            <div className="bg-white rounded-xl shadow-sm border border-warm-100 p-6">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-0.5 h-5 bg-primary-500 rounded-full flex-shrink-0" />
                <h3 className="text-sm font-semibold text-warm-900 uppercase tracking-wide">Email Content</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Subject Line *
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., Hamilton is coming to your area!"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Preview Text
                  </label>
                  <input
                    type="text"
                    value={previewText}
                    onChange={(e) => setPreviewText(e.target.value)}
                    maxLength={200}
                    className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Brief text shown in email clients before opening"
                  />
                  <p className="text-xs text-warm-500 mt-1">{previewText.length}/200 characters</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-warm-700">
                      Email Content *
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowVariableHelp(!showVariableHelp)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors flex items-center gap-1 ${showVariableHelp ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-warm-300 text-warm-500 hover:bg-warm-50'}`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        How variables work
                      </button>
                      {/* Insert Variable Dropdown */}
                      <div className="relative" ref={variableMenuRef}>
                        <button
                          type="button"
                          onClick={() => setShowVariableMenu(!showVariableMenu)}
                          className="px-3 py-1.5 text-xs font-medium border border-warm-300 text-warm-600 rounded-lg hover:bg-warm-50 transition-colors flex items-center gap-1"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                          </svg>
                          Insert Variable
                        </button>
                        {showVariableMenu && (
                          <div className="absolute right-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-warm-200 z-20 max-h-64 overflow-y-auto">
                            {TEMPLATE_VARIABLES.map((v) => {
                              const key = v.value.replace(/[{}]/g, '');
                              const resolvedVal = resolvedVariables[key] ?? PREVIEW_SAMPLE_VALUES[key];
                              return (
                                <button
                                  key={v.value}
                                  type="button"
                                  onClick={() => handleInsertVariable(v.value)}
                                  className="w-full text-left px-4 py-2 hover:bg-warm-50 transition-colors border-b border-warm-100 last:border-0"
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-sm font-medium text-warm-900">{v.label}</span>
                                    <span className="text-xs font-mono text-primary-600 flex-shrink-0">{v.value}</span>
                                  </div>
                                  {resolvedVal && (
                                    <p className="text-xs text-success-600 mt-0.5 truncate">→ "{resolvedVal}"</p>
                                  )}
                                  <p className="text-xs text-warm-400">{v.description}</p>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {/* Preview Email */}
                      {isEditing && id && (
                        <button
                          type="button"
                          onClick={handlePreview}
                          disabled={previewLoading}
                          className="px-3 py-1.5 text-xs font-medium border border-primary-300 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50"
                        >
                          {previewLoading ? 'Loading…' : 'Preview Email'}
                        </button>
                      )}
                      {/* SDA-192: Send test to myself — lands in the admin's own inbox */}
                      {isEditing && id && (
                        <button
                          type="button"
                          onClick={handleSendTest}
                          disabled={isSendingTest}
                          title="Send a test copy of this email to your own inbox"
                          className="px-3 py-1.5 text-xs font-medium border border-primary-300 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50"
                        >
                          {isSendingTest ? 'Sending…' : 'Send test to myself'}
                        </button>
                      )}
                    </div>
                  </div>
                  {testSendNotice && (
                    <div
                      className={`mb-3 p-3 rounded-lg text-xs border ${
                        testSendNotice.ok
                          ? 'bg-success-50 border-success-200 text-success-800'
                          : 'bg-danger-50 border-danger-200 text-danger-700'
                      }`}
                    >
                      {testSendNotice.text}
                    </div>
                  )}
                  {showVariableHelp && (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs">
                      <p className="font-semibold text-blue-800 mb-1.5">How variables work</p>
                      <p className="text-blue-700 mb-2">
                        Variables like <code className="bg-blue-100 px-1 rounded font-mono">{'{{show_name}}'}</code> are automatically replaced with real data when the email sends — each recipient gets their own personalised copy.
                      </p>
                      <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-blue-700">
                        <div>
                          <p className="font-semibold text-blue-800 mb-1">Need a Target Show:</p>
                          <p className="font-mono text-xs leading-relaxed">{'{{show_name}}'} {'{{theater_name}}'}<br/>{'{{available_dates}}'} {'{{pricing}}'}<br/>{'{{upcoming_shows}}'}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-blue-800 mb-1">Show OR one City:</p>
                          <p className="font-mono text-xs leading-relaxed">{'{{city}}'} {'{{state}}'}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-blue-800 mb-1">Always available:</p>
                          <p className="font-mono text-xs leading-relaxed">{'{{contact_name}}'} {'{{studio_name}}'}<br/>{'{{time_slots}}'} {'{{deposit_amount}}'}<br/>{'{{contact_email}}'} {'{{contact_phone}}'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {emptyVars.length > 0 && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs">
                      <p className="font-semibold text-amber-800 mb-1 flex items-center gap-1">
                        <span>⚠</span> With your current targeting, these variables can't be resolved and will render as a <strong>fallback</strong> when sent:
                      </p>
                      <p className="font-mono text-amber-700">{emptyVars.map(v => `{{${v}}} → ${fallbackRenderLabel(v)}`).join('   ')}</p>
                      <p className="text-amber-600 mt-1">
                        {selectedShowIds.length === 0
                          ? 'Add a Target Show to fill show-related variables (and a single Target City fills {{city}}/{{state}}) with real data, or remove these from the email.'
                          : 'Pick the matching targeting to fill these with real data, or remove them from the email.'}
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-warm-500 mb-2">
                    Write your email using the rich text editor, or toggle to HTML mode to paste custom HTML.
                  </p>
                  <RichTextEditor
                    value={htmlContent}
                    onChange={setHtmlContent}
                    placeholder="Write your email content here..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Plain Text Version
                  </label>
                  <textarea
                    value={plainTextContent}
                    onChange={(e) => setPlainTextContent(e.target.value)}
                    rows={6}
                    className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    placeholder="Plain text fallback for email clients that don't support HTML"
                  />
                </div>
              </div>
            </div>
            {/* Audience trigger — shows count + active filters */}
            <button
              type="button"
              onClick={() => setShowAudienceModal(true)}
              className="w-full text-left bg-white rounded-xl shadow-sm border border-warm-100 hover:border-primary-200 hover:bg-primary-50/30 transition-colors group px-4 py-3"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  {audienceLoading && !audience ? (
                    <span className="text-sm text-warm-400">Loading audience…</span>
                  ) : audience ? (
                    <span className={`text-sm font-semibold ${audience.estimated_audience === 0 ? 'text-amber-700' : 'text-success-700'}`}>
                      {audience.estimated_audience} {audience.estimated_audience === 1 ? 'recipient' : 'recipients'}
                    </span>
                  ) : (
                    <span className="text-sm text-warm-500">View audience</span>
                  )}
                </div>
                <span className="text-warm-400 group-hover:text-primary-500 transition-colors text-xs">View details →</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {campaignType === 'activation' ? (
                  <span className="text-[10px] px-1.5 py-0.5 bg-primary-50 text-primary-600 rounded">Dormant studios (auto-targeted)</span>
                ) : campaignType === 'faculty_invitation' ? (
                  <span className="text-[10px] px-1.5 py-0.5 bg-primary-50 text-primary-600 rounded">All invited faculty (auto-populated)</span>
                ) : (
                  <>
                    {selectedShowIds.length > 0 && selectedShowIds.map(sid => {
                      const show = shows.find(s => s.id === sid);
                      return <span key={sid} className="text-[10px] px-1.5 py-0.5 bg-primary-50 text-primary-600 rounded">{show?.name || 'Show'}</span>;
                    })}
                    {selectedCityIds.length > 0 && selectedCityIds.map(cid => {
                      const city = cities.find(c => c.id === cid);
                      return <span key={cid} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{city?.name || 'City'}</span>;
                    })}
                    {excludeBooked && <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded">Excl. booked</span>}
                    {selectedShowIds.length === 0 && selectedCityIds.length === 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-warm-100 text-warm-500 rounded">All opted-in studios</span>
                    )}
                  </>
                )}
              </div>
            </button>
          </div>

          {/* Sidebar */}
          <div className="order-last lg:order-none space-y-6">
            {/* Step 4: Schedule & Send */}
            <div className="flex items-center gap-3 pb-3 border-b border-warm-200">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold">4</div>
              <div>
                <h2 className="text-sm font-semibold text-warm-900">Step 4 of 4: Schedule & Send</h2>
                <p className="text-xs text-warm-500">Save, schedule a send date, or send now</p>
              </div>
            </div>

            {/* Actions — status-aware */}
            <div className="bg-white rounded-xl shadow-sm border-t-4 border-t-primary-500 border border-warm-100 p-6">
              <h3 className="text-sm font-semibold text-warm-900 uppercase tracking-wide mb-4">Actions</h3>

              <div className="space-y-3">
                {/* SENT — delivery metrics + failed sends */}
                {campaignStatus === 'sent' && (
                  <div className="space-y-3">
                    <div className="p-4 bg-success-50 border border-success-200 rounded-lg text-center">
                      <svg className="w-8 h-8 text-success-500 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm font-semibold text-success-800">Campaign Sent</p>
                    </div>

                    {/* Delivery Summary — sent vs failed, plus hard bounces from the bounce-scan cron */}
                    {campaign?.metrics && (
                      <div className="border border-warm-200 rounded-lg p-4">
                        <h4 className="text-xs font-semibold text-warm-700 uppercase tracking-wide mb-3">Delivery Summary</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-warm-500">Sent Successfully</span>
                            <span className="font-medium text-success-600">{campaign.metrics.total_sent - campaign.metrics.failed - campaign.metrics.bounced}</span>
                          </div>
                          {campaign.metrics.failed > 0 && (
                            <button
                              type="button"
                              onClick={() => setShowFailedSends(!showFailedSends)}
                              className="w-full flex justify-between items-center hover:bg-danger-50 -mx-1 px-1 rounded transition-colors"
                            >
                              <span className="text-danger-600 font-medium">Failed</span>
                              <span className="flex items-center gap-1 font-medium text-danger-600">
                                {campaign.metrics.failed}
                                <svg
                                  className={`w-3.5 h-3.5 transition-transform ${showFailedSends ? 'rotate-180' : ''}`}
                                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </span>
                            </button>
                          )}
                          {campaign.metrics.bounced > 0 && (
                            <div className="flex justify-between">
                              <span className="text-warning-600 font-medium">Bounced</span>
                              <span className="font-medium text-warning-600">{campaign.metrics.bounced}</span>
                            </div>
                          )}
                          {/* SDA-267: engagement from Postmark webhook timestamps */}
                          <div className="flex justify-between">
                            <span className="text-warm-500">Delivered</span>
                            <span className="font-medium text-warm-900">{campaign.metrics.delivered}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-warm-500">Opened</span>
                            <span className="font-medium text-warm-900">
                              {campaign.metrics.opened}
                              <span className="ml-1 text-xs text-warm-400">({campaign.metrics.open_rate})</span>
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-warm-500">Clicked</span>
                            <span className="font-medium text-warm-900">
                              {campaign.metrics.clicked}
                              <span className="ml-1 text-xs text-warm-400">({campaign.metrics.click_rate})</span>
                            </span>
                          </div>
                          <div className="flex justify-between pt-1 border-t border-warm-100">
                            <span className="text-warm-500">Total Attempted</span>
                            <span className="font-medium text-warm-900">{campaign.metrics.total_sent}</span>
                          </div>
                          {/* SDA-267: sign-ups stamped with this campaign's id */}
                          {campaign.metrics.attributed && (
                            <div className="pt-1 border-t border-warm-100">
                              <div className="flex justify-between">
                                <span className="text-warm-500">Attributed Sign-ups</span>
                                <span className="font-medium text-primary-600">{campaign.metrics.attributed.total}</span>
                              </div>
                              {campaign.metrics.attributed.total > 0 && (
                                <p className="text-xs text-warm-400 mt-0.5">
                                  {campaign.metrics.attributed.signups} registrations · {campaign.metrics.attributed.bookings} bookings · {campaign.metrics.attributed.inquiries} inquiries · {campaign.metrics.attributed.waitlist} waitlist
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Failed Sends List (expandable) */}
                    {showFailedSends && failedSends.length > 0 && (
                      <div className="border border-danger-200 rounded-lg overflow-hidden">
                        <div className="divide-y divide-danger-100 max-h-60 overflow-y-auto">
                          {failedSends.map((send) => {
                            const isUnsub = send.contact_id ? unsubscribedIds.has(send.contact_id) : false;
                            return (
                              <div key={send.id} className="px-4 py-2.5 text-sm flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-warm-900 truncate">{send.recipient_email}</p>
                                  {send.recipient_name && (
                                    <p className="text-xs text-warm-500">{send.recipient_name}</p>
                                  )}
                                  {send.failure_reason && (
                                    <p className="text-xs text-danger-600 mt-0.5">{send.failure_reason}</p>
                                  )}
                                </div>
                                {send.contact_id && (
                                  <button
                                    type="button"
                                    disabled={isUnsub}
                                    onClick={async () => {
                                      try {
                                        await updateAdminContact(send.contact_id!, {
                                          receives_marketing_emails: false,
                                          receives_booking_emails: false,
                                          receives_reminder_emails: false,
                                        });
                                        setUnsubscribedIds(prev => new Set([...prev, send.contact_id!]));
                                      } catch { /* ignore */ }
                                    }}
                                    className={`flex-shrink-0 px-2 py-1 text-xs rounded-md border transition-colors ${
                                      isUnsub
                                        ? 'bg-warm-100 text-warm-400 border-warm-200 cursor-default'
                                        : 'text-danger-600 border-danger-300 hover:bg-danger-50'
                                    }`}
                                  >
                                    {isUnsub ? 'Unsubscribed' : 'Unsub'}
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Unsub All Failed button */}
                        {(() => {
                          const unsubableIds = failedSends
                            .filter(s => s.contact_id && !unsubscribedIds.has(s.contact_id))
                            .map(s => s.contact_id!);
                          if (unsubableIds.length === 0) return null;
                          return (
                            <div className="px-4 py-2.5 border-t border-danger-200 bg-danger-50">
                              <button
                                type="button"
                                disabled={unsubAllLoading}
                                onClick={async () => {
                                  try {
                                    setUnsubAllLoading(true);
                                    await batchUnsubscribeContacts(unsubableIds);
                                    setUnsubscribedIds(prev => new Set([...prev, ...unsubableIds]));
                                  } catch { /* ignore */ } finally {
                                    setUnsubAllLoading(false);
                                  }
                                }}
                                className="w-full py-1.5 text-xs font-medium text-danger-700 border border-danger-300 rounded-md hover:bg-danger-100 disabled:opacity-50 transition-colors"
                              >
                                {unsubAllLoading ? 'Unsubscribing...' : `Unsubscribe All Failed (${unsubableIds.length})`}
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* SENDING — in progress with partial metrics */}
                {campaignStatus === 'sending' && (
                  <div className="space-y-3">
                    {/* Pause banner: resume_after is in the future = genuinely paused for quota */}
                    {campaign?.resume_after && new Date(campaign.resume_after) > new Date() ? (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                        <svg className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <h4 className="font-semibold text-amber-900">Sending paused</h4>
                          <p className="text-sm text-amber-700 mt-1">
                            {campaign.sent_count} of {campaign.total_recipients} sent.
                            {campaign.quota
                              ? ` Rolling 24h quota: ${campaign.quota.quota_used.toLocaleString()} / ${campaign.quota.daily_limit.toLocaleString()} used in the last 24 hours (${campaign.quota.quota_remaining.toLocaleString()} remaining).`
                              : ' Loading quota...'}
                            {` Remaining ${(campaign.total_recipients || 0) - (campaign.sent_count || 0)} emails will resume ${new Date(campaign.resume_after).toLocaleString()}.`}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg text-center">
                        <svg className="w-8 h-8 text-warning-500 mx-auto mb-2 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <p className="text-sm font-semibold text-warning-800">Sending in Progress</p>
                        <p className="text-xs text-warning-600 mt-1">
                          {campaign?.sent_count != null && campaign?.total_recipients
                            ? `${campaign.sent_count} of ${campaign.total_recipients} sent so far`
                            : 'This campaign is currently being sent to recipients.'}
                        </p>
                      </div>
                    )}

                    {campaign?.metrics && campaign.metrics.failed > 0 && (
                      <div className="border border-danger-200 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setShowFailedSends(!showFailedSends)}
                          className="w-full px-4 py-2.5 bg-danger-50 text-left flex items-center justify-between hover:bg-danger-100 transition-colors"
                        >
                          <span className="text-xs font-semibold text-danger-700 uppercase tracking-wide">
                            Failed Sends ({campaign.metrics.failed})
                          </span>
                          <svg
                            className={`w-4 h-4 text-danger-500 transition-transform ${showFailedSends ? 'rotate-180' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {showFailedSends && (
                          <div className="divide-y divide-danger-100 max-h-60 overflow-y-auto">
                            {failedSends.map((send) => (
                              <div key={send.id} className="px-4 py-2.5 text-sm">
                                <p className="font-medium text-warm-900">{send.recipient_email}</p>
                                {send.recipient_name && (
                                  <p className="text-xs text-warm-500">{send.recipient_name}</p>
                                )}
                                {send.failure_reason && (
                                  <p className="text-xs text-danger-600 mt-0.5">{send.failure_reason}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => setShowStopConfirm(true)}
                      className="w-full py-2.5 px-4 bg-danger-600 text-white rounded-lg hover:bg-danger-700 transition-colors font-medium"
                    >
                      Stop Sending
                    </button>
                  </div>
                )}

                {/* CANCELLED */}
                {campaignStatus === 'cancelled' && (
                  <div className="p-4 bg-warm-50 border border-warm-200 rounded-lg text-center">
                    <p className="text-sm font-semibold text-warm-600">Campaign Cancelled</p>
                    <p className="text-xs text-warm-500 mt-1">This campaign was cancelled and was not sent.</p>
                  </div>
                )}

                {/* SCHEDULED — show status + option to reschedule or cancel */}
                {campaignStatus === 'scheduled' && (
                  <>
                    <div className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
                      <p className="text-sm font-semibold text-primary-800">Scheduled</p>
                      <p className="text-xs text-primary-600 mt-0.5">
                        This campaign is scheduled and will be sent automatically at the scheduled time.
                      </p>
                    </div>
                    <button
                      type="submit"
                      disabled={isSaving || isSending}
                      className="w-full btn-primary disabled:opacity-50"
                    >
                      {isSaving ? 'Saving...' : 'Update Schedule'}
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={isSaving || isSending}
                      className="w-full py-2.5 px-4 text-sm font-medium text-warm-700 bg-warm-50 hover:bg-warm-100 border border-warm-200 rounded-lg disabled:opacity-50 transition-colors"
                    >
                      Unschedule (Back to Draft)
                    </button>
                    <hr className="border-warm-100 my-1" />
                    {showCampaignMissingTargeting && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        A show campaign requires one show and one city before sending.
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={handleSendClick}
                      disabled={isSaving || isSending || showCampaignMissingTargeting}
                      className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg shadow-sm disabled:opacity-50 transition-colors"
                    >
                      {isSending ? 'Sending...' : 'Send Now Instead'}
                    </button>
                  </>
                )}

                {/* DRAFT / PENDING_APPROVAL — full editing */}
                {['draft', 'pending_approval'].includes(campaignStatus) && (
                  <>
                    {showCampaignMissingTargeting && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        A show campaign requires one show and one city to save or send.
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={isSaving || isSending || showCampaignMissingTargeting}
                      className="w-full btn-primary disabled:opacity-50"
                    >
                      {isSaving
                        ? 'Saving...'
                        : campaignStatus === 'pending_approval'
                          // SDA-201: a queued campaign save persists everything
                          // and stays in the Queue — it is not a "schedule/send".
                          ? 'Save Changes'
                          : scheduledFor
                            ? 'Schedule Campaign'
                            : isEditing
                              ? 'Save Changes'
                              : 'Save & Continue'}
                    </button>
                    {/* SDA-201: "Save as Draft" nulls scheduled_for. Hide it for
                        queued campaigns so an overdue reminder can't be silently
                        demoted to a dateless draft (which loses its Queue
                        reschedule control). Drafts still get it below. */}
                    {campaignStatus !== 'pending_approval' && (
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={isSaving || isSending}
                      className="w-full py-2.5 px-4 text-sm font-medium text-warm-700 bg-warm-50 hover:bg-warm-100 border border-warm-200 rounded-lg disabled:opacity-50 transition-colors"
                    >
                      Save as Draft
                    </button>
                    )}

                    {isEditing && (
                      <>
                        <hr className="border-warm-100 my-1" />
                        {selectedShowIds.length === 0 && selectedCityIds.length === 0 && campaignType !== 'activation' && campaignType !== 'faculty_invitation' && (
                          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                            <strong>Warning:</strong> No targeting set. This will send to ALL opted-in studios.
                          </div>
                        )}
                        {campaign?.quota && (
                          <p className="text-xs text-warm-500">
                            Rolling 24h limit: {campaign.quota.quota_used}/{campaign.quota.daily_limit} sent in the last 24h | {campaign.quota.quota_remaining} remaining
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={handleSendClick}
                          disabled={isSaving || isSending || showCampaignMissingTargeting}
                          className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg shadow-sm disabled:opacity-50 transition-colors"
                        >
                          {isSending ? 'Sending...' : 'Send Campaign'}
                        </button>
                      </>
                    )}
                  </>
                )}

                {/* NEW (not editing) */}
                {!isEditing && !['draft', 'pending_approval', 'scheduled', 'sent', 'sending', 'cancelled'].includes(campaignStatus) && (
                  <>
                    {showCampaignMissingTargeting && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        A show campaign requires one show and one city to continue.
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={isSaving || showCampaignMissingTargeting}
                      className="w-full btn-primary disabled:opacity-50"
                    >
                      {isSaving ? 'Saving...' : scheduledFor ? 'Schedule Campaign' : 'Save & Continue'}
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={isSaving}
                      className="w-full py-2.5 px-4 text-sm font-medium text-warm-700 bg-warm-50 hover:bg-warm-100 border border-warm-200 rounded-lg disabled:opacity-50 transition-colors"
                    >
                      Save as Draft
                    </button>
                  </>
                )}

                <hr className="border-warm-100 my-1" />
                <Link
                  to="/admin/campaigns"
                  className="block w-full text-center py-2 text-sm text-warm-400 hover:text-warm-600 rounded-lg transition-colors"
                >
                  {['sent', 'sending', 'cancelled'].includes(campaignStatus) ? 'Back to Campaigns' : 'Cancel'}
                </Link>
              </div>
            </div>

            {/* Schedule */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-warm-900 mb-4">Schedule</h3>

              <div className="space-y-4">
                {/* Timezone selector */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Timezone
                  </label>
                  <div className="grid grid-cols-4 gap-1 p-1 bg-warm-100 rounded-lg">
                    {US_TIMEZONES.map((tz) => (
                      <button
                        key={tz.value}
                        type="button"
                        onClick={() => setScheduledTimezone(tz.value)}
                        className={`py-1.5 text-xs font-semibold rounded-md transition-all ${
                          scheduledTimezone === tz.value
                            ? 'bg-white text-warm-900 shadow-sm'
                            : 'text-warm-500 hover:text-warm-700'
                        }`}
                      >
                        {tz.abbr}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-warm-400 mt-1">
                    {selectedTzInfo.label} Time
                  </p>
                </div>

                {/* Datetime picker */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Send Date & Time ({selectedTzInfo.abbr})
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduledFor}
                    onChange={(e) => setScheduledFor(e.target.value)}
                    className="w-full px-4 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {!scheduledFor && campaignStatus === 'pending_approval' ? (
                    <p className="text-xs text-warm-500 mt-1">
                      A queued campaign needs a send date.
                    </p>
                  ) : !scheduledFor ? (
                    <p className="text-xs text-warm-500 mt-1">
                      Leave empty to save as draft
                    </p>
                  ) : null}
                </div>

                {/* Timezone preview — show equivalent times in other US timezones */}
                {scheduledFor && otherTimezones.length > 0 && (
                  <div className="border border-warm-100 rounded-lg overflow-hidden">
                    <div className="px-3 py-2 bg-warm-50 border-b border-warm-100">
                      <p className="text-xs font-medium text-warm-600 uppercase tracking-wide">
                        Also sends at
                      </p>
                    </div>
                    <div className="divide-y divide-warm-100">
                      {otherTimezones.map((tz) => (
                        <div
                          key={tz.value}
                          className="flex items-center justify-between px-3 py-2"
                        >
                          <span className="text-xs text-warm-500">
                            {tz.label}
                          </span>
                          <span className="text-sm font-medium text-warm-800">
                            {tz.display} {tz.abbr}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Scheduled status indicator */}
                {scheduledFor && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 rounded-lg">
                    <svg
                      className="w-4 h-4 text-primary-600 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-xs text-primary-700">
                      Campaign will be scheduled for{' '}
                      <span className="font-semibold">
                        {formatInTimezone(scheduledUTC!, scheduledTimezone)}{' '}
                        {selectedTzInfo.abbr}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Status info for editing */}
            {isEditing && (
              <div className="bg-warm-50 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-warm-700 mb-2">Note</h3>
                <p className="text-sm text-warm-600">
                  Only draft, scheduled, and pending approval campaigns can be edited. Once a campaign has been sent, it cannot be modified.
                </p>
              </div>
            )}

          </div>
        </div>
      </form>

      {/* Schedule Confirmation Modal */}
      {showScheduleConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 text-blue-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-warm-900">Schedule this campaign?</h3>
                <p className="text-xs text-warm-500">This will be sent automatically at the scheduled time</p>
              </div>
            </div>
            <div className="bg-warm-50 rounded-lg p-4 mb-5 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-warm-600">Campaign</span>
                <span className="font-medium text-warm-900">{name || 'Untitled'}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-warm-600">Send time</span>
                <span className="font-medium text-warm-900">
                  {scheduledFor
                    ? new Date(scheduledUTC || '').toLocaleString('en-US', {
                        weekday: 'short', month: 'short', day: 'numeric',
                        hour: 'numeric', minute: '2-digit', timeZone: scheduledTimezone,
                        timeZoneName: 'short',
                      })
                    : '—'}
                </span>
              </div>
              {audience && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-warm-600">Recipients</span>
                  <span className="font-semibold text-success-700">{audience.estimated_audience} studios</span>
                </div>
              )}
            </div>
            {campaign?.quota && audience?.estimated_audience && audience.estimated_audience > campaign.quota.daily_limit && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 font-medium">Quota Notice</p>
                <p className="text-sm text-amber-700 mt-1">
                  This campaign has <strong>{audience.estimated_audience}</strong> recipients but the rolling 24h send limit is <strong>{campaign.quota.daily_limit}</strong>.
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Emails will be sent in batches across multiple send windows. The first {campaign.quota.daily_limit} will send at the scheduled time, and the remaining {audience.estimated_audience - campaign.quota.daily_limit} will continue as capacity frees up in the rolling window.
                </p>
              </div>
            )}
            {emptyVars.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-xs font-semibold text-amber-800 mb-1">⚠ Unresolved variables will appear as plain text:</p>
                <p className="text-xs font-mono text-amber-700">{emptyVars.map(v => `{{${v}}}`).join('  ')}</p>
                <p className="text-xs text-amber-600 mt-1">Add a Target Show (and a single Target City) to fill these, or edit the email to remove them.</p>
              </div>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowScheduleConfirm(false)}
                className="flex-1 px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowScheduleConfirm(false);
                  // Re-submit the form programmatically
                  const form = document.querySelector('form');
                  if (form) form.requestSubmit();
                }}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                {emptyVars.length > 0 ? 'Schedule Anyway' : 'Yes, Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Confirmation Modal */}
      {showSendConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-warm-900 mb-2">Send Campaign Now?</h3>
            {campaign?.quota && audience?.estimated_audience && audience.estimated_audience > campaign.quota.quota_remaining ? (
              <div className="mb-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                  <p className="text-sm text-amber-800 font-medium">Quota Warning</p>
                  <p className="text-sm text-amber-700 mt-1">
                    This campaign has <strong>{audience.estimated_audience}</strong> recipients but the current 24h window has <strong>{campaign.quota.quota_remaining}</strong> remaining.
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    The first {campaign.quota.quota_remaining} emails will send now. The remaining {audience.estimated_audience - campaign.quota.quota_remaining} will continue as capacity frees up in the rolling window.
                  </p>
                </div>
                <p className="text-xs text-warm-500 mb-2 italic">
                  Your latest edits will be saved automatically before sending.
                </p>
                <p className="text-sm text-warm-500">Continue?</p>
              </div>
            ) : (
              <>
                <p className="text-warm-600 mb-1">
                  This will immediately send <strong>{subject || name}</strong> to all matching recipients.
                </p>
                <p className="text-xs text-warm-500 mb-1 italic">
                  Your latest edits will be saved automatically before sending.
                </p>
                <p className="text-sm text-warm-500 mb-6">
                  This action cannot be undone.
                </p>
              </>
            )}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowSendConfirm(false)}
                className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSendNow}
                className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors font-medium"
              >
                Yes, Send Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stop Sending Confirmation Modal */}
      {showStopConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-warm-900 mb-2">Stop Sending?</h3>
            <p className="text-warm-600 mb-1">
              {campaign?.sent_count ?? 0} of {campaign?.total_recipients ?? 0} emails have been sent so far.
            </p>
            <p className="text-sm text-warm-500 mb-6">
              The remaining emails will not be sent. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowStopConfirm(false)}
                className="flex-1 py-2.5 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleStopCampaign}
                className="flex-1 py-2.5 bg-danger-600 text-white rounded-lg hover:bg-danger-700 transition-colors font-medium"
              >
                Stop Sending
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Selector Modal */}
      {pendingChainCancel && (
        <ConfirmModal
          title="Unschedule Chain"
          message="This announcement is part of a campaign chain. Do you also want to unschedule the reminders in this chain?"
          confirmLabel="Unschedule All"
          confirmVariant="danger"
          onConfirm={async () => {
            setPendingChainCancel(false);
            // SDA-190: unschedule the whole chain back to DRAFT (scheduled →
            // draft, clears scheduled_for). Surfaces errors instead of silently
            // swallowing them; falls back to unscheduling just this one if the
            // chain endpoint reports nothing to do.
            if (!campaign?.tour_stop_id) { handleSaveDraft(); return; }
            try {
              await unscheduleChain(campaign.tour_stop_id);
              navigate('/admin/campaigns');
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Failed to unschedule chain');
            }
          }}
          onCancel={() => {
            setPendingChainCancel(false);
            // Just unschedule this one (continue to handleSaveDraft)
            handleSaveDraft();
          }}
        />
      )}

      {pendingTemplate && (
        <ConfirmModal
          title="Replace Content"
          message="This will replace your current subject and email content with the template. Continue?"
          confirmLabel="Replace"
          confirmVariant="primary"
          onConfirm={() => applyTemplate(pendingTemplate)}
          onCancel={() => setPendingTemplate(null)}
        />
      )}

      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-warm-900">Start from Template</h3>
              <button
                type="button"
                onClick={() => setShowTemplateModal(false)}
                className="p-1 text-warm-400 hover:text-warm-600 rounded"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {templatesLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-20 bg-warm-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : templates.length === 0 ? (
              <p className="text-warm-600 text-center py-8">No templates available yet.</p>
            ) : (
              <div className="space-y-3">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className="border border-warm-200 rounded-lg p-4 hover:border-primary-300 transition-colors"
                  >
                    <h4 className="font-medium text-warm-900">{template.name}</h4>
                    <p className="text-sm text-warm-600 mt-1">{template.description}</p>
                    <p className="text-xs text-warm-400 mt-1">Subject: {template.subject}</p>
                    <button
                      type="button"
                      onClick={() => handleUseTemplate(template)}
                      className="mt-3 px-3 py-1.5 text-sm font-medium text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors"
                    >
                      Use Template
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audience Modal */}
      {showAudienceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-warm-100">
              <div>
                <h3 className="text-lg font-semibold text-warm-900">Campaign Audience</h3>
                {audience && (
                  <p className={`text-sm mt-0.5 font-medium ${audience.estimated_audience === 0 ? 'text-amber-600' : 'text-success-600'}`}>
                    {audience.estimated_audience} {audience.estimated_audience === 1 ? 'studio' : 'studios'} will receive this campaign
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => { fetchAudience(); }}
                  disabled={audienceLoading}
                  className="text-xs text-primary-600 hover:underline font-medium disabled:opacity-50"
                >
                  {audienceLoading ? 'Refreshing…' : '↻ Refresh'}
                </button>
                <button type="button" onClick={() => setShowAudienceModal(false)} className="p-1 text-warm-400 hover:text-warm-600 rounded">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Active filters summary */}
            <div className="px-5 py-3 bg-warm-50 border-b border-warm-100 text-xs space-y-1.5">
              <p className="text-warm-500 font-semibold uppercase tracking-wide text-[10px]">Active Filters</p>
              <div className="flex flex-wrap gap-1.5">
                {selectedShowIds.length > 0 ? (
                  selectedShowIds.map(sid => {
                    const show = shows.find(s => s.id === sid);
                    return <span key={sid} className="inline-flex items-center px-2 py-0.5 bg-primary-50 text-primary-700 border border-primary-200 rounded-full font-medium">Show: {show?.name || sid}</span>;
                  })
                ) : null}
                {selectedCityIds.length > 0 ? (
                  selectedCityIds.map(cid => {
                    const city = cities.find(c => c.id === cid);
                    return <span key={cid} className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full font-medium">City: {city?.name || cid}{city?.state ? `, ${city.state}` : ''}</span>;
                  })
                ) : null}
                {excludeBooked && (
                  <span className="inline-flex items-center px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full font-medium">Excluding booked studios</span>
                )}
                {includeInterested && (
                  <span className="inline-flex items-center px-2 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded-full font-medium">Including interested studios</span>
                )}
                {selectedShowIds.length === 0 && selectedCityIds.length === 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 bg-warm-100 text-warm-600 border border-warm-200 rounded-full font-medium">No filters — all opted-in studios</span>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {!id ? (
                <div className="text-center py-12">
                  <p className="text-warm-600 font-medium">Save as draft first</p>
                  <p className="text-sm text-warm-400 mt-2">Once saved, you'll see exactly which studios will receive this campaign based on your targeting.</p>
                </div>
              ) : audienceLoading && !audience ? (
                <div className="space-y-3">
                  {[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-warm-100 rounded-lg animate-pulse" />)}
                </div>
              ) : audience && audience.estimated_audience === 0 ? (
                <div className="text-center py-12">
                  <p className="text-3xl mb-3">⚠</p>
                  <p className="text-warm-600 font-medium">No studios match your targeting</p>
                  <p className="text-sm text-warm-400 mt-1">Add a Target Show or Target City to reach your audience.</p>
                </div>
              ) : audience && audience.sample_recipients.length > 0 ? (
                <div className="divide-y divide-warm-50">
                  {audience.sample_recipients.map((r, i) => (
                    <div key={i} className="flex items-center justify-between py-3">
                      <div>
                        <p className="text-sm font-semibold text-warm-900">{r.studio || r.name}</p>
                        <p className="text-xs text-warm-500">
                          {r.city ? <>{r.city} · </> : null}{r.email}
                        </p>
                      </div>
                      <span className="text-xs text-warm-400 ml-4">{r.name}</span>
                    </div>
                  ))}
                  {audience.estimated_audience > audience.sample_recipients.length && (
                    <div className="pt-4 text-center">
                      <p className="text-sm text-warm-400">Showing {audience.sample_recipients.length} of {audience.estimated_audience} studios</p>
                      <button
                        type="button"
                        onClick={() => fetchAudience(true)}
                        disabled={audienceLoading}
                        className="mt-2 text-sm text-primary-600 hover:underline disabled:opacity-50 font-medium"
                      >
                        {audienceLoading ? 'Loading…' : `Load all ${audience.estimated_audience} →`}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-warm-400 text-center py-8">Loading…</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal — clean email preview */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-warm-200">
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-warm-100">
              <div>
                <h3 className="text-sm font-semibold text-warm-900">Email Preview</h3>
                <p className="text-xs text-warm-400 mt-0.5">Sample data shown where real data is unavailable</p>
              </div>
              <button
                type="button"
                onClick={() => { setShowPreviewModal(false); setPendingSend(false); setUnresolvedShowVars([]); setPerRecipientVarsState([]); }}
                className="p-1.5 text-warm-400 hover:text-warm-600 hover:bg-warm-50 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Email subject header */}
            {!previewLoading && (
              <div className="bg-warm-50 border-b border-warm-100 px-5 py-3">
                <div className="flex gap-2 text-xs items-baseline">
                  <span className="text-warm-400 flex-shrink-0">Subject</span>
                  <span className="font-semibold text-warm-900 text-sm">
                    {resolvedSubject || subject.replace(/\{\{(\w+)\}\}/g, (_m, k: string) => PREVIEW_SAMPLE_VALUES[k] ?? '') || '(no subject)'}
                  </span>
                </div>
                {!selectedShowIds.length && unresolvedShowVars.length > 0 && (
                  <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-100 mt-2">
                    No Target Show selected — show-related variables will use a fallback (e.g. {'{{city}}'} → "your area") in the actual email
                  </p>
                )}
              </div>
            )}
            {previewLoading ? (
              <div className="flex-1 flex items-center justify-center py-16">
                <div className="h-8 w-8 border-4 border-warm-200 border-t-primary-500 rounded-full animate-spin" />
              </div>
            ) : (
              <iframe
                srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>
                  body { font-family: Arial, Helvetica, sans-serif; font-size: 14px; line-height: 1.5; color: #333; margin: 0; padding: 24px; }
                  p { margin: 8px 0; }
                  ul, ol { padding-left: 24px; margin: 8px 0; }
                  li { margin: 4px 0; }
                  a { color: #1a73e8; text-decoration: underline; }
                  strong { font-weight: bold; }
                  em { font-style: italic; }
                </style></head><body>${previewHtml}</body></html>`}
                className="w-full border-0"
                style={{ height: 'calc(90vh - 280px)', maxHeight: '400px' }}
                title="Email Preview"
              />
            )}
            {pendingSend && (
              <div className="p-4 border-t border-warm-100">
                {unresolvedShowVars.length > 0 && (
                  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs font-semibold text-amber-800 mb-1">⚠ These variables will use a fallback value (not real data):</p>
                    <p className="text-xs font-mono text-amber-700">{emptyVars.map(v => `{{${v}}} → ${fallbackRenderLabel(v)}`).join('   ')}</p>
                    <p className="text-xs text-amber-600 mt-1">Add a Target Show to fill show-related variables with real data, or edit the email to remove them. Send a test to yourself to see exactly how it looks.</p>
                  </div>
                )}
                {perRecipientVarsState.length > 0 && (
                  <div className="mb-3 p-3 bg-warm-50 border border-warm-200 rounded-lg">
                    <p className="text-xs font-semibold text-warm-700 mb-1">ℹ︎ Per-recipient variables (resolve at send-time):</p>
                    <p className="text-xs font-mono text-warm-600">{perRecipientVarsState.join('  ')}</p>
                    <p className="text-xs text-warm-500 mt-1">Each recipient gets their own value filled in from their contact record.</p>
                  </div>
                )}
                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => { setShowPreviewModal(false); setPendingSend(false); setUnresolvedShowVars([]); setPerRecipientVarsState([]); }}
                    className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors text-sm"
                  >
                    Back to Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowPreviewModal(false); setPendingSend(false); handleSendNow(); }}
                    disabled={isSending}
                    className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 transition-colors font-medium text-sm"
                  >
                    {unresolvedShowVars.length > 0 ? 'Send Anyway' : 'Send Campaign'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
